import { Box, Button, Typography } from "@mui/material";
import { Duration } from "luxon";
import { useEffect } from "react";

import SimpleModal from "@/components/SimpleModal.tsx";
import { config, isNonProdStage } from "@/config";
import { useGlobalFlags } from "@/hooks/useGlobalFlags";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import { createLogger } from "@/logger";
import { forceServiceWorkerUpdate, unregisterServiceWorkers } from "@/utils/serviceWorkerUpdate";
import { resolveNextAction } from "@/utils/versionGate";

const log = createLogger("version");

const MAX_RELOADS = 5;
const RELOAD_DELAY = Duration.fromObject({ minutes: 1 });

/**
 * Automatically reloads the page when the LaunchDarkly `minimumReleaseTag` flag
 * exceeds the numeric release tag baked into the current build (`config.releaseTag`).
 *
 * A plain reload is not enough when a stale service worker serves the old bundle from cache
 * (WEB-2142), so the first attempt force-applies any pending service worker update before
 * reloading, and repeat attempts unregister the workers so the reload fetches from the network.
 *
 * Waits RELOAD_DELAY between each attempt and auto-reloads up to MAX_RELOADS times
 * (tracked in localStorage) to avoid infinite loops if the new version can't be fetched.
 * After exhausting attempts, falls back to a dialog prompting the user to reload manually.
 *
 * Outside production the gate logs its inputs and chosen action on the `version` channel. An untagged build
 * (`config.releaseTag` of 0, which shallow-checkout ephemeral deploys produce) can never be stale, and that silent
 * opt-out is otherwise indistinguishable from a flag that never arrived.
 */
export default function VersionCheckDialog() {
  const { minimumReleaseTag } = useGlobalFlags();
  const minimumVersion = minimumReleaseTag ? Number(minimumReleaseTag) : 0;
  const [reloadCount, setReloadCount] = useSafeLocalStorage("versionReloadCount", 0);
  const [reloadTarget, setReloadTarget] = useSafeLocalStorage("versionReloadTarget", 0);

  const flagsLoaded = minimumVersion > 0;
  const isStale = Boolean(flagsLoaded && config.releaseTag && minimumVersion > config.releaseTag);

  const isNewTarget = minimumVersion !== reloadTarget;
  const exhaustedReloads = !isNewTarget && reloadCount >= MAX_RELOADS;
  const nextAction = resolveNextAction({ flagsLoaded, isStale, exhaustedReloads, isNewTarget });

  useEffect(() => {
    if (!isNonProdStage) return;
    log.info("Version gate evaluated", {
      minimumReleaseTag,
      minimumVersion,
      releaseTag: config.releaseTag,
      webRelease: config.webRelease,
      buildTags: config.buildTags,
      flagsLoaded,
      isStale,
      reloadTarget,
      reloadCount,
      isNewTarget,
      exhaustedReloads,
      nextAction,
    });
  }, [
    minimumReleaseTag,
    minimumVersion,
    flagsLoaded,
    isStale,
    reloadTarget,
    reloadCount,
    isNewTarget,
    exhaustedReloads,
    nextAction,
  ]);

  useEffect(() => {
    if (!flagsLoaded) return;

    if (!isStale) {
      if (reloadCount > 0 || reloadTarget !== 0) {
        setReloadCount(0);
        setReloadTarget(0);
      }
      return;
    }

    if (exhaustedReloads) return;

    const reload = () => {
      if (isNonProdStage) {
        log.info("Reloading automatically for a stale build", {
          minimumVersion,
          releaseTag: config.releaseTag,
          reloadTarget,
          reloadCount,
          isNewTarget,
          nextAction,
        });
      }
      if (isNewTarget) {
        setReloadTarget(minimumVersion);
        setReloadCount(1);
      } else {
        setReloadCount((prev) => prev + 1);
      }
      // A repeat attempt means the forced update didn't unpin the stale bundle; drop the
      // registrations so the reload bypasses the service worker cache entirely.
      const prepare = isNewTarget ? forceServiceWorkerUpdate() : unregisterServiceWorkers();
      prepare.finally(() => window.location.reload());
    };

    const timeout = setTimeout(reload, RELOAD_DELAY.toMillis());
    return () => clearTimeout(timeout);
  }, [flagsLoaded, isStale, minimumVersion]);

  const handleRetry = () => {
    if (isNonProdStage) {
      log.info("Reloading at the user's request from the update dialog", {
        minimumVersion,
        releaseTag: config.releaseTag,
        reloadTarget,
        reloadCount,
        nextAction,
      });
    }
    setReloadCount(0);
    unregisterServiceWorkers().finally(() => window.location.reload());
  };

  return (
    <SimpleModal
      open={isStale && exhaustedReloads}
      closeModal={() => {}}
      title="Update Required"
      sx={{ maxWidth: "420px" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
        <Typography variant="body1">
          A new version of the application is available but could not be loaded automatically. Please try reloading
          manually.
        </Typography>
        <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
          If you still see this message try clearing your browser cache and reloading.
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" color="primary" onClick={handleRetry}>
            Reload Now
          </Button>
        </Box>
      </Box>
    </SimpleModal>
  );
}
